import Dexie from 'dexie';
import CryptoJS from 'crypto-js';

const ENCRYPTION_KEY = import.meta.env.VITE_ENCRYPTION_KEY || 'default_key';
class CacheDB extends Dexie {
  files: Dexie.Table<{ id: string; file: string }, string>;

  constructor() {
    super('CacheDB');
    this.version(1).stores({
      files: 'id,file',
    });
    this.files = this.table('files');
  }
}

export const cacheDB = new CacheDB();

// Turn an ArrayBuffer into a CryptoJS WordArray
function arrayBufferToWordArray(ab: ArrayBuffer): CryptoJS.lib.WordArray {
  const u8 = new Uint8Array(ab);
  const words: number[] = [];
  for (let i = 0; i < u8.length; i += 4) {
    words.push(
      ((u8[i] & 0xff) << 24) |
        ((u8[i + 1] & 0xff) << 16) |
        ((u8[i + 2] & 0xff) << 8) |
        (u8[i + 3] & 0xff),
    );
  }
  return CryptoJS.lib.WordArray.create(words, u8.length);
}

// Turn a CryptoJS WordArray back into an ArrayBuffer
function wordArrayToArrayBuffer(
  wordArray: CryptoJS.lib.WordArray,
): ArrayBuffer {
  const { words, sigBytes } = wordArray;
  const buffer = new ArrayBuffer(sigBytes);
  const u8 = new Uint8Array(buffer);
  for (let i = 0; i < sigBytes; i++) {
    const word = words[i >>> 2];
    u8[i] = (word >>> (24 - (i % 4) * 8)) & 0xff;
  }
  return buffer;
}

export function encryptFile(data: ArrayBuffer): string {
  const wordArray = arrayBufferToWordArray(data);
  // AES.encrypt returns a CipherParams object; toString() gives Base64 ciphertext
  return CryptoJS.AES.encrypt(wordArray, ENCRYPTION_KEY).toString();
}

export function decryptFile(encryptedBase64: string): ArrayBuffer {
  // AES.decrypt takes ciphertext Base64 string → WordArray
  const decrypted = CryptoJS.AES.decrypt(encryptedBase64, ENCRYPTION_KEY);
  return wordArrayToArrayBuffer(decrypted);
}

// Store file in IDB
export async function storeFileCache(key: string, fileData: ArrayBuffer) {
  await cacheDB.files.put({ id: key, file: encryptFile(fileData) });
}

// Retrieve file
export async function getFileCache(key: string) {
  const entry = await cacheDB.files.get(key);
  return entry?.file ? decryptFile(entry.file) : null;
}

// Clear cache if you want to invalidate (e.g., on logout or new version)
export async function clearFileCache(key: string) {
  await cacheDB.files.delete(key);
}
// Usage example
// await clearFileCache('productionLogTemplate');

export function encryptKeyValue(KeyValue: string): string {
  return CryptoJS.AES.encrypt(KeyValue, ENCRYPTION_KEY).toString();
}

export function decryptKeyValue(encryptedKeyValue: string): string {
  const bytes = CryptoJS.AES.decrypt(encryptedKeyValue, ENCRYPTION_KEY);
  return bytes.toString(CryptoJS.enc.Utf8);
}

export async function storeKeyValueCache(key: string, KeyValue: string) {
  const encryptedKeyValue = encryptKeyValue(KeyValue);
  await cacheDB.files.put({ id: key, file: encryptedKeyValue });
}

export async function getKeyValueCache(key: string) {
  const entry = await cacheDB.files.get(key);
  return entry?.file ? decryptKeyValue(entry.file) : null;
}

export async function clearKeyValueCache(key: string) {
  await cacheDB.files.delete(key);
}
export async function clearAllCaches() {
  try {
    await cacheDB.transaction('rw', cacheDB.tables, async () => {
      for (const table of cacheDB.tables) {
        await table.clear();
      }
    });
  } catch (error) {
    console.error('Error clearing IndexedDB tables:', error);
  }
}

export async function deleteDatabase() {
  try {
    await cacheDB.delete();
    console.log('IndexedDB database deleted successfully');
  } catch (error) {
    console.error('Error deleting IndexedDB database:', error);
  }
}
